import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";
import PulseLoader from "react-spinners/PulseLoader";
import { Dialog, Button, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import {
  useGetGeneralParameters,
  useGetMainCurrencyByCountryId,
  useGetMultiUserCurrenciesByCountryId,
} from "@hooks/useGeneralParams";
import { handleApiError } from "@utils/errorHandler";
import closeIcon from "@icons/ic-close-white.svg";
import SupplierItems from "./components/SupplierItems";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import commentIcon from "@icons/ic-comment-blue.svg";
import {
  SupplierPlanningLineUnit,
  SupplierPlanningLineDefaultSelectedUnit,
} from "@utils/constants";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";
import { getConvertedPrice } from "@utils/utils";
import {
  HeaderWrapper,
  Title,
  FormBody,
  OuterWrapper,
  FlexRow,
  SectionContainer,
  ButtonWrapper,
  CurrencyBanner,
  ExchangeRateLabel,
  ExchangeRateWrapper,
  ExchangeContainer,
  GridRow,
  GridItem,
} from "./AddEditSupplier.styled";
import "./AddEditSupplier.scss";

const AddEditSupplier = (props) => {
  const {
    t,
    isEdit,
    onClose,
    clientOrderId,
    setRefreshState,
    lineGroupId,
    countryId,
  } = props;
  const [exchangeCurrency, setExchangeCurrency] = useState([]);
  const [exchangeRate, setExchangeRate] = useState([]);
  const [isPlanningCreating, setIsPlanningCreating] = useState(false);
  const [createPlanningAPIError, setCreatePlanningAPIError] = useState("");
  const [calculateMargin, setCalculateMargin] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({});
  const [planningLines, setPlanningLines] = useState([]);
  const [prepareData, setPrepareData] = useState(false);
  const [transportTypes, setTransportTypes] = useState([]);
  const [showComment, setShowComment] = useState(false);
  const [supplierComment, setSupplierComment] = useState("");
  const [updateExchangeCurrencyInfo, setUpdateExchangeCurrencyInfo] =
    useState("");
  const generalParams = useGetGeneralParameters();
  const currencyList = generalParams.countryCurrency;
  const { unitList } = generalParams;
  const userDetails = UserInfo.getUser();
  const [platformCountryId, setPlatformCountryId] = useState(null);
  const mainCurrency = useGetMainCurrencyByCountryId(platformCountryId);
  // const mainCurrency = useGetMainCurrencyByCountryId(countryId);
  const multiCurrencyObject =
    useGetMultiUserCurrenciesByCountryId(mainCurrency);

  const momentDate = moment(new Date()).format("YYYY-MM-DD");
  const currentDate = new Date(momentDate).getTime();

  const [formData, setFormData] = useState({
    id: {
      required: false,
      value: lineGroupId ?? [],
      error: "",
    },
    clientOrderId: {
      required: false,
      value: clientOrderId ?? null,
      error: "",
    },
    services: [],
  });
  const fetchClientOrder = (id) => {
    axios
      .get(`/clientOrders/${id}?dependancies=["solutions"]`)
      .then((response) => {
        setData(response.data?.[0]);
      })
      .catch((error) => handleApiError(error));
  };

  const fetchSuppliersPlanningLines = (id) => {
    axios
      .get(
        `/clientOrders/${id}/suppliersPlanningLines?suppliersPlanningLineGroupId=${lineGroupId}`
      )
      .then((response) => {
        setPlanningLines(response.data?.supplierPlanningLine);
        setPrepareData(true);
      })
      .catch((error) => handleApiError(error));
  };

  useEffect(() => {
    if (clientOrderId) {
      fetchClientOrder(clientOrderId);
      if (isEdit) {
        fetchSuppliersPlanningLines(clientOrderId);
      } else {
        setPlanningLines([]);
        setPrepareData(true);
      }
    }
  }, [clientOrderId]);

  const fetchTranports = (pCountryId) => {
    if (!pCountryId) return;
    axios
      .get(
        `/transports?countries=[${pCountryId}]&status=1&limit=-1&orderBy=asc&orderColumn=orderIndex&prefrence=no`
      )
      .then((response) => {
        const transportTypesLocal = response.data.transports.map(
          (transportTypeItem) => {
            return {
              label: transportTypeItem.name,
              value: String(transportTypeItem.id),
            };
          }
        );
        setTransportTypes(transportTypesLocal);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (platformCountryId) {
      fetchTranports(platformCountryId);
    }
  }, [platformCountryId]);

  const getSolutionPriceObject = (solutionObj) => {
    const priceType = parseInt(solutionObj.priceType, 10);
    if ([2, 3].includes(priceType)) {
      return solutionObj?.transportPrice;
    }
    return solutionObj?.treatmentPrice;
  };

  const getSolutionDisabledStatus = (solutionData) => {
    let solutionStatus = true;

    if (
      solutionData?.country?.needSolutionAcceptance &&
      solutionData?.acceptances?.length > 0
    ) {
      solutionData.acceptances.map((acceptance) => {
        const validityStartDate = new Date(acceptance.validityStart).getTime();
        const validityEndDate = new Date(acceptance.validityEnd).getTime();
        if (
          acceptance.isActive === 1 &&
          parseInt(validityEndDate, 10) >= parseInt(currentDate, 10) &&
          parseInt(currentDate, 10) >= parseInt(validityStartDate, 10)
        ) {
          solutionStatus = false;
        }
      });
    }

    if (!solutionData?.country?.needSolutionAcceptance) {
      solutionStatus = false;
    }

    return solutionStatus;
  };

  useEffect(() => {
    if (data?.site?.platformCountryId) {
      setPlatformCountryId(data.site.platformCountryId);
    }
  }, [data]);

  useEffect(() => {
    if (data?.quoteId && prepareData && platformCountryId) {
      let newData = {};
      const items = [];
      const differentCurrencyIds = [];
      if (data?.services[0]?.serviceItems.length > 0) {
        data?.services[0]?.serviceItems?.map((item) => {
          let planningLinesInfo = {};
          if (planningLines?.length > 0 && isEdit) {
            planningLinesInfo = planningLines.find(
              (lines) => lines.clientOrderItemId === item?.clientOrderItem?.id
            );
          }
          if (item?.hesusService?.needSupplierPlanning) {
            let truckData = planningLinesInfo?.haulage;
            if (
              (!item?.hesusService?.needCombination &&
                item?.hesusService?.needCenter &&
                !item?.hesusService?.needCarrier) ||
              (item?.hesusService?.needCombination &&
                item?.hesusService?.needCenter &&
                !item?.hesusService?.needCarrier)
            ) {
              truckData = planningLinesInfo?.solution;
            }
            let selectedUnit = SupplierPlanningLineDefaultSelectedUnit;
            let unitArray = [{ label: t("SELECT_UNIT"), value: "" }];
            if (SupplierPlanningLineUnit.indexOf(item?.unit) !== -1) {
              SupplierPlanningLineUnit.map((unitData) => {
                const matchedUnitData = unitList?.find(
                  (unit) => unit.value === unitData
                );
                unitArray.push({
                  label: t(matchedUnitData?.label),
                  value: matchedUnitData?.value,
                });
              });
            }
            if (SupplierPlanningLineUnit.indexOf(item?.unit) === -1) {
              const matchedUnitData = unitList?.find(
                (unit) => unit.value === item?.unit
              );
              selectedUnit = item?.unit;
              unitArray = [
                { label: t("SELECT_UNIT"), value: "" },
                {
                  label: t(matchedUnitData?.label),
                  value: matchedUnitData?.value,
                },
              ];
            }
            if (
              ((!item?.hesusService?.needCombination &&
                item?.hesusService?.needCenter &&
                item?.hesusService?.needCarrier) ||
                (!item?.hesusService?.needCombination &&
                  item?.hesusService?.needCenter &&
                  !item?.hesusService?.needCarrier) ||
                (item?.hesusService?.needCombination &&
                  item?.hesusService?.needCenter &&
                  !item?.hesusService?.needCarrier)) &&
              planningLinesInfo?.solution?.solutionId
            ) {
              const matchedSolutionInfo = item?.solutions.find(
                (solution) =>
                  parseInt(solution.solutionId, 10) ===
                  parseInt(planningLinesInfo?.solution?.solutionId, 10)
              );
              const matchedSolutionPriceInfo =
                getSolutionPriceObject(matchedSolutionInfo);
              if (
                matchedSolutionInfo &&
                parseInt(matchedSolutionPriceInfo?.currency?.id, 10) !==
                  parseInt(data?.quoteCurrency?.id, 10) &&
                differentCurrencyIds.indexOf(
                  matchedSolutionPriceInfo.currency?.id === -1
                )
              ) {
                differentCurrencyIds.push(
                  matchedSolutionPriceInfo?.currency?.id
                );
              }
            }
            if (
              (!item?.hesusService?.needCombination &&
                item?.hesusService?.needCenter &&
                item?.hesusService?.needCarrier) ||
              (!item?.hesusService?.needCombination &&
                !item?.hesusService?.needCenter &&
                item?.hesusService?.needCarrier) ||
              (item?.hesusService?.needCombination &&
                !item?.hesusService?.needCenter &&
                item?.hesusService?.needCarrier)
            ) {
              if (
                parseInt(mainCurrency, 10) !==
                  parseInt(data?.quoteCurrency?.id, 10) &&
                differentCurrencyIds.indexOf(mainCurrency) === -1
              ) {
                differentCurrencyIds.push(mainCurrency);
              }
              if (
                parseInt(planningLinesInfo?.haulage?.currency?.id, 10) !==
                  parseInt(data?.quoteCurrency?.id, 10) &&
                differentCurrencyIds.indexOf(
                  planningLinesInfo?.haulage?.currency?.id
                ) === -1
              ) {
                differentCurrencyIds.push(
                  planningLinesInfo?.haulage?.currency?.id
                );
              }
            }
            item?.solutions?.map((solutionData, solutionIndex) => {
              const statusId = getSolutionDisabledStatus(solutionData.solution);
              item.solutions[solutionIndex].isDisabled = statusId;
            });
            items.push({
              itemInfo: item,
              clientOrderItemId: {
                value: item?.clientOrderItem?.id,
                error: "",
                required: true,
                name: "clientOrderItemId_" + item.id,
              },
              hesusService: item?.hesusService,
              planningId: {
                required: false,
                value: planningLinesInfo?.id ?? "",
                error: "",
                name: "planningId_" + item.id,
              },
              solutionId: {
                required: true,
                value: planningLinesInfo?.solution?.solutionId ?? "",
                error: "",
                name: "solutionId_" + item.id,
              },
              sellingPriceSolutionId: {
                required: true,
                value:
                  planningLinesInfo?.solution?.sellingPriceSolutionId ?? "",
                error: "",
                name: "sellingPriceSolutionId_" + item.id,
              },
              company: {
                required: true,
                value: planningLinesInfo?.haulage?.company,
                error: "",
                name: "company_" + item.id,
              },
              turnover: {
                required: true,
                value: planningLinesInfo?.turnover ?? "0",
                error: "",
                name: "turnover_" + item.id,
              },
              transportId: {
                required: true,
                value: {
                  value: truckData?.transport?.id
                    ? truckData?.transport?.id.toString()
                    : "",
                  label: truckData?.transport?.name ?? "",
                },
                error: "",
                name: "transportId_" + item.id,
              },
              trucksQuantity: {
                required: true,
                value: planningLinesInfo?.haulage?.trucksQuantity ?? "",
                error: "",
                name: "trucksQuantity_" + item.id,
              },
              price: {
                required: true,
                value: planningLinesInfo?.haulage?.purchasePrice ?? "",
                error: "",
                name: "price_" + item.id,
              },
              unit: {
                required: true,
                value: planningLinesInfo?.haulage?.purchaseUnit ?? "",
                error: "",
                name: "unit_" + item.id,
              },
              currency: {
                required: true,
                value:
                  planningLinesInfo?.haulage?.currency?.id ??
                  parseInt(mainCurrency, 10),
                error: "",
                name: "currency_" + item.id,
              },
              planningComment: {
                required: true,
                value: planningLinesInfo?.haulage?.comment ?? "",
                error: "",
                name: "planningComment_" + item.id,
              },
              marginQuantity: {
                required: true,
                value: planningLinesInfo?.quantity ?? "",
                error: "",
                name: "marginQuantity_" + item.id,
              },
              marginUnit: {
                required: true,
                value: !isEdit ? selectedUnit : planningLinesInfo?.unit ?? "",
                error: "",
                name: "marginUnit_" + item.id,
              },
              marginRate: {
                required: true,
                value: planningLinesInfo?.marginRate ?? "",
                error: "",
                name: "marginRate_" + item.id,
              },
              marginValue: {
                required: true,
                value: planningLinesInfo?.marginValue ?? "",
                error: "",
                name: "marginValue_" + item.id,
              },
              expectedMargin: {
                required: true,
                value: item?.margin ?? "",
                error: "",
                name: "expectedMargin_" + item.id,
              },
              marginUnitList: unitArray,
            });
          }
        });

        const rates = [];
        const excahngeCurrencyList = [];

        if (
          data?.services[0]?.clientOrder?.exchangeRates &&
          data?.services[0]?.clientOrder?.exchangeRates.length > 0
        ) {
          data?.services[0]?.clientOrder?.exchangeRates.map((exchange) => {
            const rateInfoData = rates.find(
              (rate) =>
                parseInt(rate.id, 10) === parseInt(exchange?.currencyFrom, 10)
            );
            if (
              !rateInfoData &&
              differentCurrencyIds.indexOf(exchange?.currencyFrom) !== -1
            ) {
              rates.push({
                exchangeRateId: exchange?.id,
                id: exchange?.currencyFrom,
                rate: exchange.exchangeRate,
                error: "",
              });
              excahngeCurrencyList.push(parseInt(exchange?.currencyFrom, 10));
            }
          });
        }

        setExchangeRate([...rates]);
        setExchangeCurrency([...excahngeCurrencyList]);

        data?.services?.map((service) => {
          newData = {
            quoteId: data?.quoteId,
            quoteCurrency: data?.quoteCurrency,
            exchangeRates: data?.exchangeRates,
            site: data?.site,
            comment: {
              required: true,
              value: planningLines?.length > 0 ? planningLines[0]?.comment : "",
              error: "",
              name: "comment_line",
            },
            services: [
              {
                id: service?.id,
                clientOrder: service?.clientOrder,
                currency: service?.currency,
                sellingPrice: service?.sellingPrice,
                serviceItems: items,
              },
            ],
          };
        });
        setFormData({ ...newData });
        setIsLoading(false);
        setSupplierComment(
          planningLines?.length > 0 ? planningLines[0]?.comment : ""
        );
      }
      setPrepareData(false);
    }
  }, [data, prepareData, platformCountryId]);

  const calculatePlanningMargin = (item, itemIndex) => {
    let isValid = true;
    const serviceIndex = 0;
    // treatment only case and treatment seprated case
    const formPurchasePrices = [];
    if (
      (!item?.hesusService?.needCombination &&
        item?.hesusService?.needCenter &&
        !item?.hesusService?.needCarrier) ||
      (item?.hesusService?.needCombination &&
        item?.hesusService?.needCenter &&
        !item?.hesusService?.needCarrier)
    ) {
      if (
        item?.solutionId?.value !== "" &&
        item?.solutionId?.value !== null &&
        item?.transportId?.value?.value !== "" &&
        item?.transportId?.value?.value !== null
      ) {
        const solutionInfo = item?.itemInfo?.solutions.find(
          (solution) =>
            parseInt(solution.solutionId, 10) ===
            parseInt(item?.solutionId?.value, 10)
        );
        formPurchasePrices.push({
          price: parseFloat(solutionInfo?.treatmentPrice?.price),
          currency: {
            id: parseInt(solutionInfo?.treatmentPrice?.currency?.id, 10),
          },
          unit: solutionInfo?.treatmentPrice?.unit,
          transportType: {
            id: item?.transportId?.value?.value,
          },
        });
      } else {
        isValid = false;
      }
    }
    // tp + tt if solution type is 1 (tratemt and tranport separated)
    if (
      !item?.hesusService?.needCombination &&
      item?.hesusService?.needCenter &&
      item?.hesusService?.needCarrier
    ) {
      if (
        item?.solutionId?.value !== "" &&
        item?.solutionId?.value !== null &&
        item?.transportId?.value?.value !== "" &&
        item?.transportId?.value?.value !== null
      ) {
        const solutionInfo = item?.itemInfo?.solutions.find(
          (solution) =>
            parseInt(solution.solutionId, 10) ===
            parseInt(item?.solutionId?.value, 10)
        );
        if (parseInt(solutionInfo.priceType, 10) === 1) {
          formPurchasePrices.push({
            price: parseFloat(solutionInfo?.treatmentPrice?.price),
            currency: {
              id: parseInt(solutionInfo?.treatmentPrice?.currency?.id, 10),
            },
            unit: solutionInfo?.treatmentPrice?.unit,
            transportType: {
              id: item?.transportId?.value?.value,
            },
          });
        }
        if (
          parseInt(solutionInfo.priceType, 10) === 2 &&
          solutionInfo?.transportPrice?.id
        ) {
          formPurchasePrices.push({
            price: parseFloat(solutionInfo?.transportPrice?.price),
            currency: {
              id: parseInt(solutionInfo?.transportPrice?.currency?.id, 10),
            },
            unit: solutionInfo?.transportPrice?.unit,
            transportType: {
              id: item?.transportId?.value?.value,
            },
          });
        }
      } else {
        isValid = false;
      }
    }

    // transport only case and treatment seprated case or tp+tt
    if (
      (!item?.hesusService?.needCombination &&
        item?.hesusService?.needCenter &&
        item?.hesusService?.needCarrier) ||
      (!item?.hesusService?.needCombination &&
        !item?.hesusService?.needCenter &&
        item?.hesusService?.needCarrier) ||
      (item?.hesusService?.needCombination &&
        !item?.hesusService?.needCenter &&
        item?.hesusService?.needCarrier)
    ) {
      if (
        item?.price?.value !== "" &&
        item?.price?.value !== null &&
        item?.currency?.value !== "" &&
        item?.currency?.value !== null &&
        item?.unit?.value !== "" &&
        item?.unit?.value !== null &&
        item?.transportId?.value?.value !== "" &&
        item?.transportId?.value?.value !== null
      ) {
        formPurchasePrices.push({
          price: parseFloat(item?.price?.value),
          currency: { id: parseInt(item?.currency?.value, 10) },
          unit: item?.unit?.value,
          transportType: {
            id: item?.transportId?.value?.value,
          },
        });
      } else {
        isValid = false;
      }
    }
    if (!item?.transportId?.value?.value) {
      isValid = false;
    }
    if (isValid) {
      const supplierExchangeData = [];
      if (exchangeRate.length > 0) {
        exchangeRate.map((rateInfo) => {
          supplierExchangeData.push({
            currencyFrom: { id: parseInt(rateInfo.id, 10) },
            currencyTo: { id: parseInt(formData?.quoteCurrency?.id, 10) },
            exchangeRate: parseFloat(rateInfo.rate),
          });
        });
      }
      const marginData = {
        exchangeRates: supplierExchangeData,
        purchasePrices: formPurchasePrices,
        sellingPrice: {
          // price: parseFloat(item?.itemInfo?.sellingPrice),
          price: getConvertedPrice(
            formData?.services[0]?.currency?.id,
            parseFloat(item?.itemInfo?.sellingPrice),
            formData?.exchangeRates
          ),
          unit: item?.itemInfo?.unit,
          currency: {
            id: parseInt(formData?.quoteCurrency?.id, 10),
          },
        },
      };
      axios
        .post(`/suppliersPlanning/calculateMargin`, marginData)
        .then((response) => {
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginRate.value = response.data.marginRate;
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginRate.error = "";
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginValue.value = response.data.margin;
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginValue.error = "";
          setFormData({ ...formData });
        })
        .catch((error) => {
          handleApiError(error);
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginRate.error = "";
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginRate.value = "";
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginValue.error = "";
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].marginValue.value = "";
          setFormData({ ...formData });
        });
    } else {
      formData.services[serviceIndex].serviceItems[itemIndex].marginRate.value =
        "";
      formData.services[serviceIndex].serviceItems[itemIndex].marginRate.error =
        "";
      formData.services[serviceIndex].serviceItems[
        itemIndex
      ].marginValue.value = "";
      formData.services[serviceIndex].serviceItems[
        itemIndex
      ].marginValue.error = "";
      setFormData({ ...formData });
    }
  };

  useEffect(() => {
    let exchangeEmpty = false;
    if (exchangeCurrency?.length > 0) {
      if (exchangeRate?.length > 0) {
        exchangeRate.map((rateInfo) => {
          if (!rateInfo.rate || rateInfo.rate === "") {
            exchangeEmpty = true;
          }
        });
      }
    }
    if (calculateMargin !== "" && !exchangeEmpty) {
      formData?.services[0].serviceItems?.map((item, iIndex) => {
        if (calculateMargin === "all") {
          calculatePlanningMargin(item, iIndex);
        }
        if (calculateMargin !== "all" && calculateMargin === iIndex) {
          calculatePlanningMargin(item, iIndex);
        }
      });
      setCalculateMargin("");
    }
    if (exchangeEmpty) {
      formData?.services[0].serviceItems?.map((item, iIndex) => {
        formData.services[0].serviceItems[iIndex].marginRate.value = "";
        formData.services[0].serviceItems[iIndex].marginRate.error = "";
        formData.services[0].serviceItems[iIndex].marginValue.value = "";
        formData.services[0].serviceItems[iIndex].marginValue.error = "";
      });
      setFormData({ ...formData });
      setCalculateMargin("");
    }
  }, [calculateMargin]);

  const onChange = (e, itemIndex, type, tid = null, spdId = null) => {
    const serviceIndex = 0;
    let transportInfo = {};
    if (type === "solution") {
      transportInfo = transportTypes.find(
        (transport) => transport.value === String(tid)
      );

      formData?.services[0].serviceItems?.map((item, iIndex) => {
        if (itemIndex === iIndex) {
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].solutionId.value = parseInt(e.target.value, 10);
          formData.services[serviceIndex].serviceItems[
            itemIndex
          ].solutionId.error = "";
        }
        formData.services[serviceIndex].serviceItems[
          iIndex
        ].sellingPriceSolutionId.value = spdId;
        formData.services[serviceIndex].serviceItems[
          iIndex
        ].sellingPriceSolutionId.error = "";
        formData.services[serviceIndex].serviceItems[iIndex].transportId.value =
          transportInfo;
        formData.services[serviceIndex].serviceItems[iIndex].transportId.error =
          "";
      });
    } else {
      let fieldValue = "";
      if (["trucksQuantity", "price", "marginQuantity"].indexOf(type) !== -1) {
        fieldValue = e.target.value;
      }
      if (["unit", "currency", "marginUnit"].indexOf(type) !== -1) {
        fieldValue = e.value;
      }
      if (type === "transportId") {
        fieldValue = e;
      }
      if (type === "company") {
        fieldValue = e;
      }
      if (type === "planningComment") {
        fieldValue = e;
      }

      formData?.services[0].serviceItems?.map((item, iIndex) => {
        if (itemIndex === iIndex) {
          if (type === "trucksQuantity") {
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].trucksQuantity.value = fieldValue;
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].trucksQuantity.error = "";
          }
          if (type === "price") {
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].price.value = fieldValue;
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].price.error = "";
          }
          if (type === "unit") {
            formData.services[serviceIndex].serviceItems[itemIndex].unit.value =
              fieldValue;
            formData.services[serviceIndex].serviceItems[itemIndex].unit.error =
              "";
          }
          if (type === "marginQuantity") {
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].marginQuantity.value = fieldValue;
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].marginQuantity.error = "";
          }
          if (type === "marginUnit") {
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].marginUnit.value = fieldValue;
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].marginUnit.error = "";
          }
          if (type === "currency") {
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].currency.value = fieldValue;
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].currency.error = "";
          }
          if (type === "company") {
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].company.value = fieldValue;
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].company.error = "";
          }
          if (type === "planningComment") {
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].planningComment.value = fieldValue;
            formData.services[serviceIndex].serviceItems[
              itemIndex
            ].planningComment.error = "";
          }
        }
        if (type === "transportId") {
          formData.services[serviceIndex].serviceItems[
            iIndex
          ].transportId.value = fieldValue;
          formData.services[serviceIndex].serviceItems[
            iIndex
          ].transportId.error = "";
        }
      });
    }
    setFormData({ ...formData });
    if (["transportId"].indexOf(type) !== -1) {
      setCalculateMargin("all");
    }
    if (["price", "unit"].indexOf(type) !== -1) {
      setCalculateMargin(itemIndex);
    }
    if (["currency"].indexOf(type) !== -1) {
      setUpdateExchangeCurrencyInfo(itemIndex);
    }
    if (["solution"].indexOf(type) !== -1) {
      setUpdateExchangeCurrencyInfo("all");
    }
  };

  useEffect(() => {
    if (updateExchangeCurrencyInfo !== "" || updateExchangeCurrencyInfo === 0) {
      const currencyArray = [];
      const rates = [];
      const quoteCurrency = [];

      if (formData?.services[0].serviceItems?.length > 0) {
        formData?.services[0].serviceItems?.map((item, itemIndex) => {
          if (item?.solutionId?.value) {
            const solutionInfo = item?.itemInfo?.solutions.find(
              (solution) =>
                parseInt(solution.solutionId, 10) ===
                parseInt(item?.solutionId?.value, 10)
            );
            const solutionInfoObj = getSolutionPriceObject(solutionInfo);
            if (
              solutionInfo &&
              quoteCurrency.indexOf(
                parseInt(solutionInfoObj?.currency?.id, 10)
              ) === -1 &&
              parseInt(formData?.quoteCurrency?.id, 10) !==
                parseInt(solutionInfoObj?.currency?.id, 10)
            ) {
              quoteCurrency.push(parseInt(solutionInfoObj?.currency?.id, 10));
            }
          }
          if (item?.currency?.value) {
            if (
              quoteCurrency.indexOf(parseInt(item?.currency?.value, 10)) ===
                -1 &&
              parseInt(formData?.quoteCurrency?.id, 10) !==
                parseInt(item?.currency?.value, 10)
            ) {
              quoteCurrency.push(parseInt(item?.currency?.value, 10));
            }
          }
        });
      }
      if (quoteCurrency?.length > 0) {
        quoteCurrency.map((currencyInfo) => {
          currencyArray.push(parseInt(currencyInfo, 10));

          const rateInfoData = exchangeRate.find(
            (rate) => rate.id === currencyInfo
          );
          if (rateInfoData) {
            rates.push(rateInfoData);
          } else {
            let rateValue = "";
            let exchangeRateIdVal = null;
            const quoteExchangeRate = data?.exchangeRates?.find(
              (exchangeRateInfo) =>
                exchangeRateInfo.currencyFrom === currencyInfo
            );
            if (quoteExchangeRate) {
              rateValue = quoteExchangeRate?.exchangeRate;
            }

            const quoteItemExchangeRate =
              data?.services[0]?.clientOrder?.exchangeRates?.find(
                (exchangeRateInfo) =>
                  exchangeRateInfo.currencyFrom === currencyInfo
              );
            if (quoteItemExchangeRate) {
              rateValue = quoteItemExchangeRate?.exchangeRate;
              exchangeRateIdVal = quoteItemExchangeRate?.id;
            }
            rates.push({
              id: currencyInfo,
              rate: rateValue,
              error: "",
              exchangeRateId: exchangeRateIdVal,
            });
          }
        });
      }
      setExchangeCurrency([...currencyArray]);
      setExchangeRate([...rates]);
      setCalculateMargin(updateExchangeCurrencyInfo);
      setUpdateExchangeCurrencyInfo("");
    }
  }, [updateExchangeCurrencyInfo]);

  const getExchangeError = (currencyId) => {
    const rateInfo = exchangeRate.find((rate) => rate.id === currencyId);
    return rateInfo ? rateInfo?.error : "";
  };

  const getExchangeValue = (currencyId) => {
    const rateInfo = exchangeRate.find((rate) => rate.id === currencyId);
    return rateInfo ? String(rateInfo?.rate) : "";
  };

  const onExchangeRateChange = (e, currencyId) => {
    let rates = [];
    let value = "";
    // if value is not blank, then test the regex

    if (e.target.value === "" || /^-?\d*[.]?\d{0,2}$/.test(e.target.value)) {
      value = e.target.value;
    }
    const rateInfoData = exchangeRate.find(
      (rate) => parseInt(rate.id, 10) === parseInt(currencyId, 10)
    );

    if (rateInfoData) {
      rates = exchangeRate.map((rateInfo) => {
        const rateObject = { ...rateInfo };
        if (rateInfo.id === currencyId) {
          rateObject.rate = value;
          rateObject.error = "";
        }
        return rateObject;
      });
    } else {
      rates = exchangeRate;
      rates.push({
        id: currencyId,
        rate: value,
        error: "",
        exchangeRateId: null,
      });
    }
    setExchangeRate([...rates]);
    setCalculateMargin("all");
  };

  const validateForm = (data) => {
    const serviceIndex = 0;
    let isFormValid = true;
    data?.services[0]?.serviceItems?.map((item, itemIndex) => {
      // for solution section
      if (
        (!item?.hesusService?.needCombination &&
          item?.hesusService?.needCenter &&
          item?.hesusService?.needCarrier) ||
        (!item?.hesusService?.needCombination &&
          item?.hesusService?.needCenter &&
          !item?.hesusService?.needCarrier) ||
        (item?.hesusService?.needCombination &&
          item?.hesusService?.needCenter &&
          !item?.hesusService?.needCarrier)
      ) {
        if (!item?.solutionId?.value) {
          isFormValid = false;

          data.services[serviceIndex].serviceItems[itemIndex].solutionId.error =
            t("SUPPLIERS_PLANNING_FORM_SOLUTION_MISSING");
        }
        if (!item?.marginQuantity?.value && item?.marginQuantity?.value !== 0) {
          isFormValid = false;

          data.services[serviceIndex].serviceItems[
            itemIndex
          ].marginQuantity.error = t("GENERAL_MANDATORY_ERROR");
        }
        if (!item?.marginUnit?.value || item?.marginUnit?.value === "") {
          isFormValid = false;

          data.services[serviceIndex].serviceItems[itemIndex].marginUnit.error =
            t("GENERAL_MANDATORY_ERROR");
        }
        if (!item?.marginRate?.value && item?.marginRate?.value !== 0) {
          isFormValid = false;

          data.services[serviceIndex].serviceItems[itemIndex].marginRate.error =
            t("SUPPLIERS_PLANNING_FORM_MARGIN_MISSING");
        }
        if (
          !item?.hesusService?.needCombination &&
          item?.hesusService?.needCenter &&
          !item?.hesusService?.needCarrier
        ) {
          if (
            !item?.transportId?.value?.value &&
            item?.transportId?.value?.value === ""
          ) {
            isFormValid = false;
            data.services[serviceIndex].serviceItems[
              itemIndex
            ].transportId.error = t("GENERAL_MANDATORY_ERROR");
          }
        }
      }
      if (
        (!item?.hesusService?.needCombination &&
          item?.hesusService?.needCenter &&
          item?.hesusService?.needCarrier) ||
        (!item?.hesusService?.needCombination &&
          !item?.hesusService?.needCenter &&
          item?.hesusService?.needCarrier) ||
        (item?.hesusService?.needCombination &&
          !item?.hesusService?.needCenter &&
          item?.hesusService?.needCarrier)
      ) {
        if (
          item?.company?.value?.length === 0 ||
          item?.company?.value === undefined ||
          item?.company?.value === null ||
          !item?.company?.value
        ) {
          isFormValid = false;
          data.services[serviceIndex].serviceItems[itemIndex].company.error = t(
            "GENERAL_MANDATORY_ERROR"
          );
        }
        if (
          !item?.transportId?.value?.value &&
          item?.transportId?.value?.value === ""
        ) {
          isFormValid = false;
          data.services[serviceIndex].serviceItems[
            itemIndex
          ].transportId.error = t("GENERAL_MANDATORY_ERROR");
        }
        if (!item?.price?.value && item?.price?.value === "") {
          isFormValid = false;
          data.services[serviceIndex].serviceItems[itemIndex].price.error = t(
            "GENERAL_MANDATORY_ERROR"
          );
        }
        if (!item?.unit?.value && item?.unit?.value === "") {
          isFormValid = false;
          data.services[serviceIndex].serviceItems[itemIndex].unit.error = t(
            "GENERAL_MANDATORY_ERROR"
          );
        }
        if (!item.currency.value) {
          isFormValid = false;
          data.services[serviceIndex].serviceItems[itemIndex].currency.error =
            t("GENERAL_MANDATORY_ERROR");
        }
        if (
          (!item?.hesusService?.needCombination &&
            !item?.hesusService?.needCenter &&
            item?.hesusService?.needCarrier) ||
          (item?.hesusService?.needCombination &&
            !item?.hesusService?.needCenter &&
            item?.hesusService?.needCarrier)
        ) {
          if (
            !item?.marginQuantity?.value &&
            item?.marginQuantity?.value !== 0
          ) {
            isFormValid = false;
            data.services[serviceIndex].serviceItems[
              itemIndex
            ].marginQuantity.error = t("GENERAL_MANDATORY_ERROR");
          }
          if (!item?.marginUnit?.value || item?.marginUnit?.value === "") {
            isFormValid = false;
            data.services[serviceIndex].serviceItems[
              itemIndex
            ].marginUnit.error = t("GENERAL_MANDATORY_ERROR");
          }
          if (!item?.marginRate?.value && item?.marginRate?.value !== 0) {
            isFormValid = false;
            data.services[serviceIndex].serviceItems[
              itemIndex
            ].marginRate.error = t("SUPPLIERS_PLANNING_FORM_MARGIN_MISSING");
          }
        }
      }
    });
    let rates = [];
    if (exchangeCurrency?.length > 0) {
      if (exchangeRate?.length > 0) {
        rates = exchangeRate.map((rateInfo, raetIndex) => {
          const tempData = { ...rateInfo };
          if (!rateInfo.rate || rateInfo.rate === "") {
            isFormValid = false;
            tempData.error = t("FIELD_REQUIRED");
          }
          return tempData;
        });
      }
      if (exchangeRate?.length === 0) {
        exchangeCurrency.map((exchange) => {
          rates.push({ id: exchange, rate: "", error: t("FIELD_REQUIRED") });
        });
      }
    }
    if (!isFormValid) {
      setFormData({ ...data });
      if (exchangeRate?.length > 0) setExchangeRate([...rates]);
    }
    return isFormValid;
  };
  const getFormData = () => {
    const planningExchangeData = [];
    const planningLinesData = [];
    if (exchangeRate.length > 0) {
      exchangeRate.map((rateInfo) => {
        planningExchangeData.push({
          id: rateInfo?.exchangeRateId,
          currencyFrom: rateInfo.id,
          currencyTo: parseInt(formData?.quoteCurrency?.id, 10),
          exchangeRate: rateInfo.rate,
        });
      });
    }
    if (formData?.services[0]?.serviceItems.length > 0) {
      formData?.services[0]?.serviceItems?.map((item) => {
        let solutionInfo = {};
        let haulageInfo = {};
        if (
          (!item?.hesusService?.needCombination &&
            item?.hesusService?.needCenter &&
            item?.hesusService?.needCarrier) ||
          (!item?.hesusService?.needCombination &&
            item?.hesusService?.needCenter &&
            !item?.hesusService?.needCarrier) ||
          (item?.hesusService?.needCombination &&
            item?.hesusService?.needCenter &&
            !item?.hesusService?.needCarrier)
        ) {
          const matchedSolutionInfo = item?.itemInfo?.solutions.find(
            (solution) =>
              parseInt(solution.solutionId, 10) ===
              parseInt(item?.solutionId?.value, 10)
          );
          const matchedSolutionPriceInfo =
            getSolutionPriceObject(matchedSolutionInfo);
          solutionInfo = {
            solutionId: matchedSolutionInfo.solutionId,
            sellingPriceSolutionId: matchedSolutionInfo.id,
            purchasePrice: parseFloat(matchedSolutionPriceInfo?.price),
            purchaseUnit: matchedSolutionPriceInfo?.unit,
            currency: matchedSolutionPriceInfo?.currency,
            product: matchedSolutionInfo?.product,
            centre: matchedSolutionInfo?.centre,
            transport: { id: parseInt(item?.transportId?.value?.value, 10) },
          };
        }
        if (
          (!item?.hesusService?.needCombination &&
            item?.hesusService?.needCenter &&
            item?.hesusService?.needCarrier) ||
          (!item?.hesusService?.needCombination &&
            !item?.hesusService?.needCenter &&
            item?.hesusService?.needCarrier) ||
          (item?.hesusService?.needCombination &&
            !item?.hesusService?.needCenter &&
            item?.hesusService?.needCarrier)
        ) {
          haulageInfo = {
            company: item.company.value,
            transport: { id: parseInt(item?.transportId?.value?.value, 10) },
            purchasePrice: item.price.value,
            purchaseUnit: item.unit.value,
            currency: { id: item.currency.value },
            trucksQuantity:
              item.trucksQuantity.value !== ""
                ? item.trucksQuantity.value
                : null,
            comment: item.planningComment.value,
          };
        }
        planningLinesData.push({
          id: isEdit ? item.planningId.value : null,
          jobsiteId: data?.site?.id,
          clientOrderItemId: item.clientOrderItemId.value,
          quantity: item.marginQuantity.value,
          unit: item.marginUnit.value,
          marginValue: item.marginValue.value,
          marginRate: item.marginRate.value,
          turnover: item.turnover.value,
          solution: solutionInfo,
          haulage: haulageInfo,
          createdBy: userDetails.id,
          updatedBy: userDetails.id,
          createdAt: "",
          updatedAt: "",
          comment: formData?.comment?.value,
        });
      });
    }
    const formSubmitData = {
      exchangeRates: planningExchangeData,
      suppliersPlanningLines: planningLinesData,
    };
    return formSubmitData;
  };
  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsPlanningCreating(true);
      setCreatePlanningAPIError("");
      if (!isEdit) {
        axios
          .post(
            `/clientOrders/${clientOrderId}/suppliersPlanningLines`,
            getFormData()
          )
          .then(() => {
            setIsPlanningCreating(false);
            setRefreshState(true);
            onClose();
          })
          .catch((error) => {
            setIsPlanningCreating(false);
            setCreatePlanningAPIError(error.response?.data?.message ?? "");
          });
      } else {
        axios
          .put(
            `/clientOrders/${clientOrderId}/suppliersPlanningLines`,
            getFormData()
          )
          .then(() => {
            setIsPlanningCreating(false);
            setRefreshState(true);
            onClose();
          })
          .catch((error) => {
            setIsPlanningCreating(false);
            setCreatePlanningAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  const showCommentBox = () => {
    setShowComment(true);
  };

  useEffect(() => {
    setFormData({
      ...formData,
      comment: {
        value: supplierComment,
        error: "",
        required: true,
      },
    });
  }, [supplierComment]);

  return (
    <Dialog width="80rem">
      <div className="add-edit-supplier-planning">
        <HeaderWrapper>
          <Title>
            {!isEdit
              ? t("SUPPLIERS_PLANNING_FORM_TITLE_NEW")
              : t("SUPPLIERS_PLANNING_FORM_TITLE_EDIT")}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image
              src={closeIcon}
              alt="close Enquiry price popup"
              styles={{ width: "auto" }}
            />
          </Button>
        </HeaderWrapper>
        {exchangeCurrency.length > 0 && (
          <ExchangeRateWrapper>
            <ExchangeContainer>
              <GridRow>
                <CurrencyBanner>
                  {exchangeCurrency.map((exchange, exchangeIndex) => {
                    return (
                      <React.Fragment
                        key={"sp-quote-item-exchange-" + exchangeIndex}
                      >
                        <ExchangeRateLabel
                          color={
                            getExchangeError(exchange) !== "" ? "red" : "#fff"
                          }
                        >
                          {exchangeIndex === 0
                            ? t("QUOTE_EXCHANGE_RATE")
                            : "RATE"}{" "}
                          (
                          {currencyList[exchange] +
                            " -> " +
                            currencyList[formData.quoteCurrency.id]}
                          )
                        </ExchangeRateLabel>
                        <TextInput
                          type="number"
                          onChange={(e) => {
                            onExchangeRateChange(e, exchange);
                          }}
                          disabled={false}
                          name="selling-price-exchange-rate-label"
                          id="selling-price-exchange-rate-label"
                          value={getExchangeValue(exchange)}
                          errorStyles={{ "font-style": "italic" }}
                          labelStyles={
                            getExchangeError(exchange) !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            getExchangeError(exchange) !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          containerStyles={{
                            textAlign: "left",
                            width: "10%",
                          }}
                          error={getExchangeError(exchange)}
                        />
                      </React.Fragment>
                    );
                  })}
                </CurrencyBanner>
              </GridRow>
            </ExchangeContainer>
          </ExchangeRateWrapper>
        )}
        <FormBody>
          <OuterWrapper>
            <FlexRow>
              <SectionContainer>
                {isLoading && (
                  <div className="suggestions-list-loading">
                    <PulseLoader color="#1bace4" size={15} />
                  </div>
                )}
                {!isLoading && platformCountryId && (
                  <div>
                    <SupplierItems
                      quoteCurrency={formData?.quoteCurrency}
                      exchangeRates={formData?.exchangeRates}
                      formData={formData}
                      isEdit={isEdit}
                      onChange={onChange}
                      transportTypes={transportTypes}
                      platformCountryId={platformCountryId}
                      multiCurrencyObject={multiCurrencyObject}
                    />
                    {((!showComment && !isEdit) ||
                      (isEdit && formData?.comment?.value === "")) && (
                      <GridRow>
                        <GridItem
                          fontSize="14px"
                          fontWeight="700"
                          color="#15aee5"
                          padding="5px 10px"
                          textAlign="left"
                          gap="0rem"
                          leftRadius="0px"
                          rightRadius="0px"
                          textDecoration="underline"
                          onClick={showCommentBox}
                        >
                          <Image
                            src={commentIcon}
                            className="add-edit-supplier-list__image-action-icon"
                            alt={t("SUPPLIERS_PLANNING_FORM_COMMENT")}
                            title={t("SUPPLIERS_PLANNING_FORM_COMMENT")}
                          />
                          <span style={{ cursor: "pointer" }}>
                            {t("SUPPLIERS_PLANNING_FORM_COMMENT")}
                          </span>
                        </GridItem>
                      </GridRow>
                    )}
                    {(showComment || formData?.comment?.value !== "") && (
                      <GridRow column="1fr">
                        <GridItem>
                          <CKEditorText
                            descriptionData={supplierComment}
                            setDescriptionData={setSupplierComment}
                          />
                        </GridItem>
                      </GridRow>
                    )}
                  </div>
                )}
              </SectionContainer>
            </FlexRow>
          </OuterWrapper>
        </FormBody>
        <ButtonWrapper>
          {createPlanningAPIError && <div>{createPlanningAPIError}</div>}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              marginRight: "0.32rem",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isPlanningCreating && <ClipLoader size={15} color="#fff" />}
              {!isPlanningCreating && t("SUBMIT")}
            </>
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditSupplier.propTypes = {
  t: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  lineGroupId: PropTypes.number,
};

AddEditSupplier.defaultProps = {
  isEdit: false,
  lineGroupId: "",
};
export default withTranslation()(AddEditSupplier);
